import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpService } from './http.service';

export interface ErrorLogData {
  device: string;
  env: string;
  headers: string;
  ip?: string;
  message: string;
  method?: string;
  note?: string;
  payload?: string;
  routeName?: string;
  service: string;
  stack: string;
  timestamp: string;
  user?: string;
  frontend: boolean; // Flag to distinguish frontend errors
  url?: string;
  lineNumber?: number;
  columnNumber?: number;
  fileName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorLoggingService {
  private readonly LOG_ENDPOINT = 'log-issue'; // Adjust this to your actual endpoint
  private isLogging = false; // Prevent recursive logging

  constructor(private httpService: HttpService) {}

  /**
   * Collects device and environment information
   */
  private getDeviceInfo(): string {
    return navigator.userAgent;
  }

  /**
   * Gets current environment
   */
  private getEnvironment(): string {
    // You can enhance this to detect environment from your config
    return window.location.hostname === 'localhost' ? 'development' : 'production';
  }

  /**
   * Gets user information from localStorage
   */
  private getUserInfo(): string | null {
    const userJSON = localStorage.getItem('userDB');
    const tokenJSON = localStorage.getItem('token');
    
    if (userJSON) {
      try {
        const user = JSON.parse(userJSON);
        return JSON.stringify({
          uid: user.uid || user.id,
          email: user.email
        });
      } catch (e) {
        return null;
      }
    }
    
    return null;
  }

  /**
   * Extracts stack trace information
   */
  private extractStackInfo(error: Error): { fileName?: string; lineNumber?: number; columnNumber?: number } {
    const stack = error.stack || '';
    const match = stack.match(/at\s+(.+?):(\d+):(\d+)/);
    
    if (match) {
      return {
        fileName: match[1],
        lineNumber: parseInt(match[2], 10),
        columnNumber: parseInt(match[3], 10)
      };
    }
    
    return {};
  }

  /**
   * Sends error to logging service
   */
  async logError(
    message: string,
    error?: Error,
    additionalData?: any
  ): Promise<void> {
    // Prevent recursive logging
    if (this.isLogging) {
      console.warn('⚠️ Skipping error logging to prevent recursion');
      return;
    }

    this.isLogging = true;

    try {
      const stackInfo = error ? this.extractStackInfo(error) : {};
      
      const errorLogData: ErrorLogData = {
        device: this.getDeviceInfo(),
        env: this.getEnvironment(),
        headers: JSON.stringify({
          'user-agent': navigator.userAgent,
          'accept-language': navigator.language,
          'referer': document.referrer
        }),
        message,
        service: 'frontend-app',
        stack: error?.stack || 'No stack trace available',
        timestamp: new Date().toISOString(),
        user: this.getUserInfo(),
        frontend: true, // Frontend flag
        url: window.location.href,
        ...stackInfo,
        ...additionalData
      };

      // Send to your logging endpoint (no retry, no timeout)
      await this.httpService.post(this.LOG_ENDPOINT, errorLogData).toPromise();
      
      console.log('✅ Error logged successfully');
    } catch (loggingError) {
      console.error('❌ Failed to log error (no retry):', loggingError);
      // Don't throw the error to avoid infinite loops
    } finally {
      this.isLogging = false;
    }
  }

  /**
   * Logs HTTP errors with additional context
   */
  async logHttpError(
    message: string,
    error: any,
    requestUrl?: string,
    requestMethod?: string,
    requestPayload?: any
  ): Promise<void> {
    const additionalData = {
      method: requestMethod,
      routeName: this.extractRouteName(requestUrl),
      payload: requestPayload ? JSON.stringify(requestPayload) : null,
      url: requestUrl
    };

    await this.logError(message, error, additionalData);
  }

  /**
   * Extracts route name from URL
   */
  private extractRouteName(url?: string): string | undefined {
    if (!url) return undefined;
    
    // Extract meaningful route name from URL
    const pathname = new URL(url).pathname;
    return pathname.replace(/^\//, '').replace(/\//g, '-') || 'unknown-route';
  }

}
